import React, { useEffect, useCallback, useState } from "react";
import ConnexionForm from "../ConnexionForm";
import SignupForm from "../SignupForm";
import ForgetPassword from "../ForgetPassword";
import VerificationCode from "./VerificationCode/VerificationCode";
import VerificationModal from "../SignupForm/components/VerificationModal";
import { useDispatch } from "react-redux";
import getStyles from "./SignInSignUpContainer.styles";
import { Typography, Box } from "@mui/material";
import { ModalCard, Avatar } from "@dishopsaas/design-system";
import { useSignInSignUp } from "../../hooks/useSignInSignUp";
import type { SignUpData } from "../../1_types/signinTypes";
import CloseButton from "../CloseButton";
import {
  handleConnectionService,
  handleForgetPasswordService,
  handleVerifyCodeService,
  resendCodeService,
} from "./SignInSignUpContainer.services";
import { isDarkTheme } from "../../utils";
import { verificationCodeService } from "./VerificationCode/VerificationCode.services";

const SignInSignUpContainer: React.FC = () => {
  const {
    userSignupError,
    userConnectionError,
    resetPasswordError,
    signUp,
    showForgetPassword,
    name,
    logoUrl,
    email,
    handleUserSignUp,
    handleFacebookOrGoogleSignIn,
    handleResetLogin,
    showVerificationModal,
    handleShowVerificationModal,
    secondaryColor,
    handleShowForgetPassword,
    requireVerification,
    verificationEmail,
  } = useSignInSignUp();
  const dispatch = useDispatch();
  const [password, setPassword] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState(verificationCodeService.resetTimer());
  const styles = getStyles();

  useEffect(() => {
    handleResetLogin();
  }, [handleResetLogin]);

  const handleVerifyCode = useCallback(
    async (code: string): Promise<void> => {
      await handleVerifyCodeService(
        dispatch,
        verificationEmail,
        password,
        code,
        setVerificationError,
        setLoading,
        setTime,
      );
    },
    [dispatch, verificationEmail, password],
  );
  const sendNewCode = () => {
    resendCodeService(dispatch, verificationEmail, password);
  };

  const onSubmitSignUp = (
    email: string,
    password: string,
    nom: string,
    prenom: string,
    telephone: string,
    phoneObj?: any,
    newsletter: boolean = false,
  ) => {
    const signUpData: SignUpData = {
      email,
      password,
      nom,
      prenom,
      telephone,
      phoneObj,
      newsletter,
    };
    handleUserSignUp(signUpData);
  };
  const handleForgetPassword = useCallback(
    (email) => {
      handleForgetPasswordService(dispatch, email);
    },

    [dispatch],
  );
  const handleConnection = useCallback(
    (email, password) => {
      handleConnectionService(
        dispatch,
        email,
        password,
        setPassword,
        setVerificationError,
      );
    },
    [dispatch],
  );

  const handleContinueToSignup = () => {
    handleShowVerificationModal(true);
  };

  const getTitle = () => {
    if (signUp) {
      return "INSCRIPTION";
    }
    return `BIENVENUE CHEZ ${name?.toUpperCase()}`;
  };

  const getSubtitle = () => {
    if (signUp) {
      return null;
    }
    return (
      "Créez un compte " + name + " en quelques secondes ou Connectez-vous"
    );
  };

  return (
    <div>
      {requireVerification && (
        <VerificationCode
          onClearError={() => setVerificationError("")}
          email={verificationEmail}
          secondaryColor={secondaryColor}
          dark={isDarkTheme()}
          onVerify={handleVerifyCode}
          errorMessage={verificationError}
          onResend={sendNewCode}
          loading={loading}
          setTime={setTime}
          time={time}
        />
      )}
      {!requireVerification && !signUp && !showForgetPassword && (
        <Box>
          <Box style={styles.signInContainer}>
            <Avatar src={logoUrl} size="lg" shape="rounded" />
            <Box sx={{ gap: 1 }}>
              <Typography variant="h4" sx={styles.welcomeTitle}>
                {getTitle()}
              </Typography>
              {getSubtitle() && (
                <Typography variant="body2" sx={styles.welcomeSubtitle}>
                  {getSubtitle()}
                </Typography>
              )}
            </Box>
          </Box>
          <ConnexionForm
            onSubmit={handleConnection}
            facebookOrGoogleSignIn={handleFacebookOrGoogleSignIn}
            errorMessage={userConnectionError}
            onContinueToSignup={handleContinueToSignup}
          />
        </Box>
      )}

      {!requireVerification && signUp && !showForgetPassword && (
        <div>
          <Box style={styles.boxContainer}>
            <Typography variant="h4" sx={styles.welcomeTitle}>
              {getTitle()}
            </Typography>
          </Box>
          <SignupForm
            onSubmit={onSubmitSignUp}
            errorMessage={userSignupError}
          />
        </div>
      )}

      <ModalCard
        open={showVerificationModal}
        onClose={() => handleShowVerificationModal(false)}
      >
        <VerificationModal
          email={email}
          onClose={() => handleShowVerificationModal(false)}
        />
      </ModalCard>

      <ModalCard
        sxModal={styles.container}
        maxWidth={440}
        open={!requireVerification && !signUp && showForgetPassword}
        onClose={() => handleShowForgetPassword(false)}
      >
        <Box>
          <CloseButton
            sx={styles.close}
            onClick={() => handleShowForgetPassword(false)}
          />
          <ForgetPassword
            onSubmit={handleForgetPassword}
            errorMessage={resetPasswordError}
          />
        </Box>
      </ModalCard>
    </div>
  );
};

export default SignInSignUpContainer;
